// @givmo/board — EVERY new word the officer's pages add for the email an ask for signatures sends
// (CBG-RC10-MAIL, backend `board_minutes.md` §S17.8) and for a withdrawn request staying legible
// (CBG-RC10-WITHDRAWN, §S18.3).
//
// ══ WHICH WORDS ARE APPROVED ════════════════════════════════════════════════════════════════════
//
// All of them: copy round CBG-RC10-COPY2's page, cut votenote's
// (`scratchpad/2026-10-14-votenote-copy-round/copy-review.html`), approved as drafted (Jonathan,
// 2026-10-15 06:01 PDT, "go with your recs"). No frame draws them (RC10's `c2` and `c3` predate
// both rows). What they reuse is reused from its one definition, never restated here: each asked
// director's email line is CBG-OPS1-CIRC-WEB's (`consent-delivery-words.ts`, approved), the
// unnamed person is CBG-RC11-COPY1's (`record-person-words.ts`), and "counts for nothing" is the
// approved withdrawal dialog's (`minutes-signatures-words.ts`, CBG-RC10-COPY1).
//
// ⚠ Plan §3.2 and §3.4 bind every line: what Board recorded and what it sent, never what the law
// requires, never "must" or "should". A person is named in full, and no pronoun stands for a named
// person. "Emailed" says Board handed the message to the mail service, never that anyone read it.

import { recordPersonWords } from '../records/record-person-words';

/** A name the read resolved, or the approved words for one it didn't. */
const person = (name: string | null): string => name ?? recordPersonWords.unnamed;

export const minutesAskWords = {
  // ── CBG-RC10-MAIL ───────────────────────────────────────────────────────────────────────────────

  /**
   * Not drawn: after the approved `signaturesBeforeAsk` on the Signatures card, so the officer
   * knows before the one press that asking emails the directors (the orchestrator's M-C4), as the
   * circulate dialog says it of a consent.
   */
  askEmails: 'Asking emails each of them.',

  // ── CBG-RC10-WITHDRAWN: the requests' history on the minutes page ─────────────────────────────

  /** Not drawn: the heading over the withdrawn requests. */
  earlierRequests: 'Earlier requests for signatures',
  /**
   * Not drawn: one withdrawn request, newest first. The reason is the officer's own words, shown as
   * they gave them.
   */
  withdrawnRequest: (input: {
    askedBy: string | null;
    askedOn: string;
    withdrawnBy: string | null;
    withdrawnOn: string;
    reason: string;
  }): string =>
    `Asked by ${person(input.askedBy)} on ${input.askedOn}. Withdrawn by ${person(input.withdrawnBy)} on ${input.withdrawnOn}: ${input.reason}`,

  // ── CBG-RC10-WITHDRAWN: the mark under a "Signed to approve" line in the record's history ──────

  /** Not drawn: the backend's `approval_signature_mark`, worded. */
  mark: {
    request_withdrawn:
      'The request for these signatures was withdrawn, so this one counts for nothing.',
    earlier_version: 'This signed an earlier version of these minutes, so it counts for nothing.',
    approved_another_way:
      'These minutes were approved another way, so this one counts for nothing.',
  } as Readonly<Record<string, string>>,
} as const;
